import { IonItem, IonItemSliding } from "@ionic/react";
import { useState } from "react";

import styles from "@components/carePlan/CarePlanActionItem.module.css";
import CarePlanCard from "@components/CarePlanCard";
import ClientAvatar from "@components/ClientAvatar";
import SlidingItemOptions from "@components/SlidingItemOptions";
import BodyCopy from "@components/typography/BodyCopy";
import useDestroyCarePlanActionItem from "@hooks/mutations/useDestroyCarePlanActionItem";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import {
  CarePlanActionItemToggleCompletedDocument,
  CarePlanActionItemToggleCompletedMutation,
  CarePlanActionItemToggleCompletedMutationVariables,
  CarePlanActionItemFragment as CarePlanActionItemType,
  GetJourneyWithPlanQuery,
  GoalStatusEnum
} from "@typing/Generated";
import { carePlanActionItemHasChanges } from "@utils/modelUtils";
import { isMobile } from "@utils/platformUtils";

type Props = {
  carePlanActionItem: CarePlanActionItemType;
  clientView?: boolean;
  handleMarkedCompleted?: (completed: boolean, id: string) => void;
  journey: GetJourneyWithPlanQuery["journey"];
  onEdit: (actionItem: CarePlanActionItemType) => void;
};

const CarePlanActionItem = ({
  carePlanActionItem,
  clientView = true,
  handleMarkedCompleted,
  journey,
  onEdit
}: Props) => {
  const [submittingId, setSubmittingId] = useState<String | null>(null);
  const popoverOpener = useOpener();

  const destroyCarePlanActionItem = useDestroyCarePlanActionItem(journey);

  const [carePlanActionItemToggleCompleted] = useMutationWithValidation<
    CarePlanActionItemToggleCompletedMutation,
    CarePlanActionItemToggleCompletedMutationVariables
  >(CarePlanActionItemToggleCompletedDocument, "carePlanActionItem");

  const handleEdit = () => {
    popoverOpener.close();
    onEdit(carePlanActionItem);
  };

  const handleDestroy = () => {
    popoverOpener.close();
    destroyCarePlanActionItem({
      id: carePlanActionItem.id
    });
  };

  const handleCompletedChange = (completed: boolean) => {
    setSubmittingId(carePlanActionItem.id);
    carePlanActionItemToggleCompleted({
      completed,
      id: carePlanActionItem.id
    }).then(() => {
      setSubmittingId(null);
      handleMarkedCompleted?.(completed, carePlanActionItem.id);
    });
  };

  const sanitizedTitle = useSanitizeHtml(carePlanActionItem.title, journey.id, carePlanActionItem);

  const hasEditModeChanges =
    carePlanActionItem.goal.status === GoalStatusEnum.EDIT_MODE &&
    carePlanActionItemHasChanges(
      carePlanActionItem,
      journey.goals?.find(goal => goal.id === carePlanActionItem.goal.activeGoalId)
    );

  const cardExtraClassNames = [];

  if (hasEditModeChanges) {
    cardExtraClassNames.push(styles.editModeCard);
  } else if (!clientView) {
    cardExtraClassNames.push(styles.expertViewCard);
  }

  return (
    <IonItemSliding disabled={!isMobile()} key={carePlanActionItem.id}>
      <SlidingItemOptions
        options={[
          {
            color: "primary-500",
            icon: "edit",
            onClick: handleEdit,
            textKey: "dictionary.edit"
          }
        ]}
        side="start"
      />
      <IonItem className={styles.slidingItem} lines="none">
        <CarePlanCard
          body={
            <div className="flex justify-between items-center gap-10">
              <BodyCopy className={styles.overrideSanitizedStyles}>{sanitizedTitle}</BodyCopy>
              <div className="flex gap-sm items-center">
                {journey.clientRoles.length > 1 &&
                  carePlanActionItem.assignments?.map((assignee, index) => (
                    <ClientAvatar
                      clientId={assignee.id}
                      color={
                        index === 0
                          ? "primary-500"
                          : index === 1
                            ? "secondary-500"
                            : index === 2
                              ? "secondary-500"
                              : "primary"
                      }
                      firstName={assignee.firstName}
                      journeyId={journey.id}
                      key={assignee.id}
                      lastName={assignee.lastName}
                      size="normal"
                    />
                  ))}
              </div>
            </div>
          }
          border={undefined}
          checkboxProps={{
            checked: carePlanActionItem.completed,
            disabled: submittingId === carePlanActionItem.id,
            name: `carePlanActionItem-${carePlanActionItem.id}`,
            onChange: handleCompletedChange
          }}
          className={cardExtraClassNames.join(" ")}
          key={carePlanActionItem.id}
          popoverItems={[
            {
              icon: "edit",
              onClick: handleEdit,
              textKey: "dictionary.edit"
            },
            {
              icon: "trash",
              onClick: handleDestroy,
              textKey: "dictionary.delete"
            }
          ]}
        />
      </IonItem>
      <SlidingItemOptions
        options={[
          {
            color: "danger-500",
            icon: "trash",
            onClick: handleDestroy,
            textKey: "dictionary.delete"
          }
        ]}
        side="end"
      />
    </IonItemSliding>
  );
};

export default CarePlanActionItem;
